import { useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";

import { SiteNav, SiteFooter } from "../SiteChrome";
import { legalStyle } from "./legalStyles";

/*
  One layout for every long-form legal document (/privacy, /terms).

  The document itself is data — see privacyContent.js / termsContent.js — so this
  file only owns the shell: the sky band the floating nav pill is transparent
  over, the sticky contents rail, and the block renderer below.
*/

const renderBlock = (block, i) => {
  const [kind, value] = block;

  switch (kind) {
    case "h":
      return <h3 key={i}>{value}</h3>;

    case "ul":
      return (
        <ul key={i}>
          {value.map((item, j) => <li key={j}>{item}</li>)}
        </ul>
      );

    case "note":
      return (
        <div className="lg-note" key={i}>
          <p>{value}</p>
        </div>
      );

    case "dl":
      return (
        <div className="lg-card" key={i}>
          <dl>
            {value.map(([label, detail], j) => (
              <div key={j} style={{ display: "contents" }}>
                <dt>{label}</dt>
                <dd>
                  {/* Emails and the website are the only values worth making
                      actionable; everything else is plain text. */}
                  {detail.includes("@") && !detail.includes("\n")
                    ? <a href={`mailto:${detail}`}>{detail}</a>
                    : detail.startsWith("www.")
                      ? <a href={`https://${detail}`} target="_blank" rel="noopener noreferrer">{detail}</a>
                      : detail}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      );

    case "p":
    default:
      return <p key={i}>{value}</p>;
  }
};

/**
 * @param doc one of the documents in ./privacyContent.js or ./termsContent.js.
 */
export default function LegalPage({ doc }) {
  const navigate = useNavigate();
  const heroRef = useRef(null);

  // A route change lands mid-page otherwise, since the router preserves scroll.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [doc.slug]);

  useEffect(() => {
    document.title = `${doc.title} · Sentio Mind`;
  }, [doc.title]);

  // The pill carries the two sibling documents plus a way home. Home is a
  // route change; the other two are either a route change or a no-op reset to
  // the top of the document you are already reading.
  const navLinks = [
    { label: "Home", id: "home", href: "/" },
    { label: "Privacy", id: "privacy", href: "/privacy" },
    { label: "Terms", id: "terms", href: "/terms" },
  ];

  const handleNavigate = (id) => {
    if (id === "home") {
      navigate("/");
      return;
    }
    if (id === doc.slug) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    navigate(`/${id}`);
  };

  const contents = doc.sections.map((section) => (
    <li key={section.id}>
      <a href={`#${section.id}`}>
        <b>{String(section.num).padStart(2, "0")}</b>
        <span>{section.title}</span>
      </a>
    </li>
  ));

  return (
    <div className="lg-page">
      <style>{legalStyle}</style>

      <SiteNav
        variant="floating"
        heroRef={heroRef}
        links={navLinks}
        onNavigate={handleNavigate}
      />

      {/* ── SKY BAND ─────────────────────────────────────────────────────────── */}
      <header className="lg-hero" ref={heroRef}>
        <div className="lg-hero-inner">
          <span className="lg-eyebrow">{doc.eyebrow}</span>
          <h1>
            {doc.title}
            <span>{doc.titleAccent}</span>
          </h1>
          <p className="lg-hero-sub">{doc.sub}</p>
          <div className="lg-meta">
            <span>Effective Date: {doc.effective}</span>
            <span>Last Updated: {doc.updated}</span>
          </div>
        </div>
      </header>

      {/* ── DOCUMENT ─────────────────────────────────────────────────────────── */}
      <main className="lg-main">
        <nav className="lg-toc" aria-label="Contents">
          <p className="lg-toc-title">Contents</p>
          <ol>{contents}</ol>
        </nav>

        <div className="lg-doc">
          <details className="lg-toc-mobile">
            <summary>Contents</summary>
            <ol>{contents}</ol>
          </details>

          <div className="lg-intro">
            {doc.intro.map((text, i) => <p key={i}>{text}</p>)}
          </div>

          {doc.sections.map((section) => (
            <section className="lg-section" id={section.id} key={section.id}>
              <div className="lg-section-head">
                <span className="lg-num" aria-hidden="true">
                  {String(section.num).padStart(2, "0")}
                </span>
                <h2>{section.title}</h2>
              </div>
              {section.blocks.map(renderBlock)}
            </section>
          ))}

          {doc.closing && (
            <div className="lg-closing">
              <h2>{doc.closing.title}</h2>
              {doc.closing.paras.map((text, i) => <p key={i}>{text}</p>)}
              <ul>
                {doc.closing.items.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </div>
          )}

          {doc.crosslink && (
            <div className="lg-crosslink">
              <p>{doc.crosslink.text}</p>
              <Link className="lg-btn" to={doc.crosslink.to}>
                {doc.crosslink.label} <span aria-hidden="true">→</span>
              </Link>
            </div>
          )}
        </div>
      </main>

      <SiteFooter contactEmail="support@sentiomind.in" />
    </div>
  );
}
